// One countdown timer used throughout the booking flow so every page treats
// expiry consistently. Two visual variants on the same data:
//
//   • <CountdownPill>  — small pill (results topbar, drawer foot).
//                        Green when ≥ 60s remain, amber 30–59s, red < 30s,
//                        with an "expired" outline when seconds <= 0.
//   • <HoldTimer />    — full-width bar with clock icon + copy (used in
//                        the booking sidebar on passengers / reprice / payment).
//
// Both auto-tick via useCountdown(), so callers just pass an absolute target
// epoch in ms (or null to render nothing).

import { useCountdown, fmtCountdown } from "../lib/useCountdown";

function tone(secs: number | null): "ok" | "warn" | "danger" | "expired" {
  if (secs == null) return "ok";
  if (secs <= 0) return "expired";
  if (secs < 30) return "danger";
  if (secs < 60) return "warn";
  return "ok";
}

// ─── pill (topbar / drawer foot) ────────────────────────────────────────────

export function CountdownPill({
  targetMs,
  prefix = "Results valid",
  expiredText = "Results expired",
  hideWhenExpired = false,
}: {
  targetMs: number | null;
  prefix?: string;
  expiredText?: string;
  /** When true, render nothing once the countdown hits zero. Used in
   *  contextual surfaces (e.g. the fare-rules drawer foot) where a stale
   *  "expired" pill is more confusing than helpful — the API call will
   *  surface the real error if the hold is truly gone. */
  hideWhenExpired?: boolean;
}) {
  const secs = useCountdown(targetMs);
  const t = tone(secs);
  if (hideWhenExpired && (t === "expired" || secs == null)) return null;
  return (
    <div className={`countdown-pill ${t}`}>
      <span className="dot" />
      {t === "expired"
        ? expiredText
        : `${prefix} · ${fmtCountdown(secs)}`}
    </div>
  );
}

// ─── bar (sidebar / drawer body) ────────────────────────────────────────────

export function HoldTimer({
  targetMs,
  prefix = "Booking holds for",
}: {
  targetMs: number | null;
  prefix?: string;
}) {
  const secs = useCountdown(targetMs);
  // No target, or already expired → render nothing. The next API call (save
  // passengers / book) will surface the real error if the hold is truly gone;
  // a stale "expired" warning on every fresh booking is worse than silence.
  if (secs == null || secs <= 0) return null;
  const t = tone(secs);
  return (
    <div className={`hold-timer ${t}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
      </svg>
      <span className="copy">
        {prefix} <b>{fmtCountdown(secs)}</b>
      </span>
    </div>
  );
}
